import React, { useEffect, useState } from 'react';
import { api, BACKEND_URL } from '../utils/api';
import { exportToCSV, exportToExcel } from '../utils/export';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, Package, Download, FileText, File } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const Reports = () => {
  const [topProducts, setTopProducts] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [productsRes, customersRes, allCustomersRes] = await Promise.all([
        api.reports.getTopProducts(),
        api.reports.getTopCustomers(),
        api.customers.getAll()
      ]);
      setTopProducts(productsRes.data);
      setTopCustomers(customersRes.data);
      setCustomers(allCustomersRes.data);
    } catch (error) {
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type, format) => {
    try {
      let response, data, filename, formattedData;
      
      if (type === 'products') {
        response = await api.reports.exportProducts();
        data = response.data.data;
        filename = 'Relatorio_Produtos';
        
        formattedData = data.map(p => ({
          'Nome do Produto': p.name,
          'Categoria': p.category,
          'Custo Unitário (R$)': p.cost_price ? parseFloat(p.cost_price).toFixed(2) : '0.00',
          'Preço de Venda (R$)': p.price ? parseFloat(p.price).toFixed(2) : '0.00',
          'Estoque Atual': p.stock,
          'Estoque Mínimo': p.min_stock
        }));

      } else if (type === 'sales') {
        response = await api.reports.exportSales();
        data = response.data.data;
        filename = 'Relatorio_Vendas';
        
        // Novo agrupador mensal por abas
        formattedData = {};

        data.forEach(s => {
          const dateObj = new Date(s.created_at);
          const dateStr = dateObj.toLocaleDateString('pt-BR') + ' ' + dateObj.toLocaleTimeString('pt-BR', {hour: '2-digit', minute:'2-digit'});
          
          // Cria o nome da aba no formato "MM_AAAA"
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const year = dateObj.getFullYear();
          const sheetName = `${month}_${year}`;
          
          if (!formattedData[sheetName]) {
            formattedData[sheetName] = [];
          }
          
          const itemsStr = s.items ? s.items.map(i => `${i.quantity}x ${i.product_name}`).join(' | ') : '';
          
          formattedData[sheetName].push({
            'Data da Venda': dateStr,
            'Cliente': s.customer_name || 'Avulso',
            'Itens Comprados': itemsStr,
            'Valor Total (R$)': s.total ? parseFloat(s.total).toFixed(2) : '0.00',
            'Pagamento': s.payment_method ? s.payment_method.toUpperCase() : '-',
            'Status': s.status ? s.status.toUpperCase() : '-'
          });
        });

      } else if (type === 'customers') {
        response = await api.reports.exportCustomers();
        data = response.data.data;
        filename = 'Relatorio_Clientes';
        
        formattedData = data.map(c => {
          let birth = 'Não informada';
          if (c.birth_date) {
            const d = new Date(c.birth_date + "T12:00:00");
            birth = d.toLocaleDateString('pt-BR');
          }
          return {
            'Nome do Cliente': c.name,
            'Telefone': c.phone || 'Não informado',
            'CPF/CNPJ': c.cpf_cnpj || 'Não informado',
            'Aniversário': birth,
            'Saldo Devedor (R$)': c.balance ? parseFloat(c.balance).toFixed(2) : '0.00'
          };
        });
      }
      
      if (format === 'csv') {
        exportToCSV(formattedData, filename);
      } else {
        exportToExcel(formattedData, filename);
      }
      
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    }
  };

  const handleExportPDF = async () => {
    if (!selectedCustomer) {
      toast.error('Selecione um cliente primeiro');
      return;
    }

    try {
      const response = await api.reports.exportCustomerPDF(selectedCustomer);

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `extrato_cliente.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Extrato PDF gerado com sucesso!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao gerar extrato PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground font-mono">Carregando relatórios...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
        <h1 className="text-5xl font-bold uppercase tracking-tight">Relatórios</h1>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button
            onClick={() => handleExport('products', 'excel')}
            className="bg-primary hover:bg-primary/90 font-bold uppercase"
            data-testid="export-products-excel"
          >
            <Download className="mr-2 h-4 w-4" /> Produtos Excel
          </Button>
          <Button
            onClick={() => handleExport('sales', 'excel')}
            className="bg-primary hover:bg-primary/90 font-bold uppercase"
            data-testid="export-sales-excel"
          >
            <Download className="mr-2 h-4 w-4" /> Vendas Excel
          </Button>
          <Button
            onClick={() => handleExport('customers', 'csv')}
            className="bg-secondary hover:bg-secondary/80 font-bold uppercase"
            data-testid="export-customers-csv"
          >
            <FileText className="mr-2 h-4 w-4" /> Clientes CSV
          </Button>
        </div>
      </div>

      <Card className="bg-card border-border" data-testid="pdf-export-card">
        <CardHeader>
          <CardTitle className="text-xl uppercase tracking-wide flex items-center gap-2">
            <File className="h-5 w-5 text-primary" /> Exportar Extrato de Cliente
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 w-full">
            <label className="text-xs uppercase tracking-wider mb-2 block">Selecione o Cliente</label>
            <Select onValueChange={setSelectedCustomer}>
              <SelectTrigger className="font-mono w-full">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent className="bg-card border-border max-h-60">
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={handleExportPDF} 
            className="bg-destructive hover:bg-destructive/90 font-bold uppercase w-full md:w-auto"
            disabled={!selectedCustomer}
          >
            <FileText className="mr-2 h-4 w-4" /> Gerar PDF
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border" data-testid="top-products-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Package className="h-6 w-6 text-primary" />
              <CardTitle className="text-xl uppercase tracking-wide">Produtos Mais Vendidos</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {topProducts.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">Nenhuma venda registrada</p>
            ) : (
              <div className="space-y-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topProducts.slice(0, 5)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: '#a1a1aa', fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis tick={{ fill: '#a1a1aa', fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                      labelStyle={{ color: '#fafafa' }}
                    />
                    <Bar dataKey="quantity" fill="#f97316" />
                  </BarChart>
                </ResponsiveContainer>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-muted-foreground font-mono uppercase text-xs">Produto</TableHead>
                      <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Qtd</TableHead>
                      <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Receita</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topProducts.map((product, index) => (
                      <TableRow key={product.product_id} className="border-border/50" data-testid={`top-product-${index}`}>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell className="font-mono text-right">{product.quantity}</TableCell>
                        <TableCell className="font-mono font-bold text-primary text-right">
                          R$ {product.revenue.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="top-customers-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Users className="h-6 w-6 text-primary" />
              <CardTitle className="text-xl uppercase tracking-wide">Melhores Clientes</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {topCustomers.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">Nenhuma venda registrada</p>
            ) : (
              <div className="space-y-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topCustomers.slice(0, 5)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: '#a1a1aa', fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis tick={{ fill: '#a1a1aa', fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                      labelStyle={{ color: '#fafafa' }}
                    />
                    <Bar dataKey="total_spent" fill="#f97316" />
                  </BarChart>
                </ResponsiveContainer>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-muted-foreground font-mono uppercase text-xs">Cliente</TableHead>
                      <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Visitas</TableHead>
                      <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Total Gasto</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topCustomers.map((customer, index) => (
                      <TableRow key={customer.customer_id} className="border-border/50" data-testid={`top-customer-${index}`}>
                        <TableCell className="font-medium">{customer.name}</TableCell>
                        <TableCell className="font-mono text-right">{customer.visit_count}</TableCell>
                        <TableCell className="font-mono font-bold text-primary text-right">
                          R$ {customer.total_spent.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
